/**
 * @file FreeformGrid.js
 * @description A responsive grid component that renders content blocks in either a desktop grid layout or
 * a mobile column layout. It dynamically calculates positions, sizes, and layouts based on screen dimensions
 * and configuration data. Supports variable content types through the Component system.
 *
 * The grid maintains consistent square cell sizing in desktop mode and translates to a vertical stack in mobile view.
 * It handles loading states, empty content states, and complex positioning of content blocks.
 */
import {
    useState,
    useEffect,
    useLayoutEffect,
    useCallback,
    useRef,
} from 'react';
import './FreeformGrid.css';
import Spinner from '../ui/spinners/Spinner';
import { Component } from '../Component';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import { getOverlappingContainerIds } from '../../utils/overlapUtils';
import { getText } from '../../i18n';

const FreeformGrid = ({
    fetchedContainers,
    fetchedMobileConfiguration,
    navigationStack,
    portalName,
    portalId,
    onShowPortalView,
    isPreview = false,
    previewMode = null,
    isViewLive = false,
}) => {
    const [newContainers, setNewContainers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasContent, setHasContent] = useState(false);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 786);

    const COLUMN_COUNT = 96;
    const GAP = 1;
    const [rowCount, setRowCount] = useState(120); // Initial value

    const SIDE_NAV_BAR_WIDTH = 130;
    const [GRID_WIDTH, setGridWidth] = useState(1366 - SIDE_NAV_BAR_WIDTH); // State for GRID_WIDTH

    const [items, setItems] = useState([]);
    const [gridHeight, setGridHeight] = useState(0);
    const [rawBlocks, setRawBlocks] = useState([]);
    const [contentBlocks, setContentBlocks] = useState([]);
    const [cellSize, setCellSize] = useState(0);
    const [overlappingIds, setOverlappingIds] = useState(new Set());
    const mainAreaRef = useRef(null); // Create a ref for the main area

    // Function to calculate the highest row value
    const calculateRowCount = containers => {
        if (containers.length === 0) return 0; // Return 0 if there are no containers
        return (
            containers.reduce((max, container) => {
                return Math.max(max, container.end.row);
            }, 0) + 1
        );
    };

    useEffect(() => {
        setIsLoading(true);
        setHasContent(fetchedContainers && fetchedContainers.length > 0);

        if (fetchedContainers && fetchedContainers.length > 0) {
            const updatedContainers = fetchedContainers.map(container => ({
                ...container,
                temporaryId: container.id,
            }));

            setNewContainers(updatedContainers);
        } else {
            setNewContainers([]);
            setIsLoading(false);
        }
    }, [fetchedContainers]);

    // Populate rawBlocks from containers only once when the component mounts
    useEffect(() => {
        if (newContainers.length > 0) {
            const highestRowCount = calculateRowCount(newContainers);
            setRowCount(highestRowCount);
            setRawBlocks(newContainers);
            setOverlappingIds(getOverlappingContainerIds(newContainers));
        } else {
            setRawBlocks([]);
            setOverlappingIds(new Set());
        }
    }, [newContainers]);

    // ------------------------------------------------------------------
    // 1) Calculate Grid Items & Force Square Cells
    // ------------------------------------------------------------------

    const calculateGridItems = useCallback(() => {
        // Use GRID_WIDTH instead of measuring container width
        const totalGapWidth = GAP * (COLUMN_COUNT - 1);

        // Force squares by making itemHeight = columnWidth
        const columnWidth = (GRID_WIDTH - totalGapWidth) / COLUMN_COUNT;
        const itemHeight = columnWidth; // squares
        setCellSize(columnWidth);

        // Now, how many rows fit in the current viewport?
        const viewportHeight = window.innerHeight;
        //const rows = Math.floor((viewportHeight + GAP) / (itemHeight + GAP));
        const rows = rowCount;
        const totalItems = rows * COLUMN_COUNT;

        setItems(Array.from({ length: totalItems }, (_, index) => index + 1));
        setGridHeight(rows * (itemHeight + GAP) - GAP);
    }, [COLUMN_COUNT, GAP, GRID_WIDTH, rowCount]);

    const updateContentBlocks = useCallback(() => {
        const totalGapWidth = GAP * (COLUMN_COUNT - 1);
        const columnWidth = (GRID_WIDTH - totalGapWidth) / COLUMN_COUNT;

        // Force height = columnWidth so they're squares
        const squareSize = columnWidth;

        const calculatedBlocks = rawBlocks.map(block => {
            const width =
                (block.end.col - block.start.col + 1) * squareSize +
                (block.end.col - block.start.col) * GAP;
            const height =
                (block.end.row - block.start.row + 1) * squareSize +
                (block.end.row - block.start.row) * GAP;

            return { ...block, width, height };
        });

        setContentBlocks(calculatedBlocks);
    }, [rawBlocks, COLUMN_COUNT, GAP, GRID_WIDTH]);

    useEffect(() => {
        if (rawBlocks.length > 0) {
            updateContentBlocks();
        } else if (fetchedContainers.length === 0) {
            setIsLoading(false);
        }
    }, [rawBlocks, updateContentBlocks, fetchedContainers]);

    // Finalize loading state when content blocks are processed
    useEffect(() => {
        if (contentBlocks.length > 0 || (!hasContent && !isLoading)) {
            setIsLoading(false);
        }
    }, [contentBlocks, hasContent, isLoading]);

    // ------------------------------------------------------------------
    // 2) Use useLayoutEffect for immediate, consistent measurement
    // ------------------------------------------------------------------
    useLayoutEffect(() => {
        const handleResize = () => {
            // COMMENTING THESE TO MAINTAIN FIXED GRID WIDTH OF 1366
            /*if (mainAreaRef.current) {
        setGridWidth(mainAreaRef.current.clientWidth);
      }*/
            calculateGridItems();
            updateContentBlocks();
            setIsMobile(window.innerWidth <= 786); // Update isMobile based on window width
        };

        handleResize();

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [calculateGridItems, updateContentBlocks]);

    // ------------------------------------------------------------------
    // 3) Use useLayoutEffect for re-render accounting for scrollbar
    // ------------------------------------------------------------------
    useLayoutEffect(() => {
        setTimeout(() => {
            calculateGridItems();
            updateContentBlocks();
        }, 0);
    }, [calculateGridItems, updateContentBlocks]);

    // ----------------------------------------
    // MOBILE
    // ----------------------------------------

    const [mobileItems, setMobileItems] = useState([]);

    // Update mobileItems based on fetchedMobileConfiguration
    useEffect(() => {
        if (
            newContainers.length > 0 &&
            fetchedMobileConfiguration &&
            fetchedMobileConfiguration.length > 0
        ) {
            const orderedMobileItems = fetchedMobileConfiguration
                .map(id => newContainers.find(container => container.id === id))
                .filter(item => item !== undefined); // Filter out any undefined items

            setMobileItems(orderedMobileItems);
        } else {
            setMobileItems([]);
        }
    }, [newContainers, fetchedMobileConfiguration]);

    const effectiveIsMobile =
        previewMode === 'mobile'
            ? true
            : previewMode === 'desktop'
              ? false
              : isMobile;

    const shouldShowNoContent =
        !isLoading &&
        ((effectiveIsMobile && (!mobileItems || mobileItems.length === 0)) ||
            (!effectiveIsMobile &&
                (!contentBlocks || contentBlocks.length === 0)));

    return (
        <div className="grid-area" ref={mainAreaRef}>
            {isLoading && <Spinner width={55} className="loadingSpinner" />}

            {shouldShowNoContent && (
                <NotFoundBox
                    backgroundColor="#f0f4ff"
                    title={getText('msg_53c03704e29d')}
                    description={getText('msg_8379a6d76c50')}
                />
            )}

            {!isLoading &&
                !shouldShowNoContent &&
                (effectiveIsMobile ? (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '10px',
                            gap: '10px',
                        }}
                    >
                        {mobileItems.map((block, index) => (
                            <div key={block.id || `mobile-${index}`}>
                                <div
                                    style={{
                                        width: '100%',
                                        height: 'fit-content',
                                    }}
                                >
                                    <Component
                                        ID={block.id}
                                        Name={block.name}
                                        Subtitle={block.title}
                                        ContentTypeID={block.type}
                                        HelpBoxText={block.helpBoxText}
                                        AcknowledgmentReq={
                                            block.acknowledgmentType
                                        }
                                        AcknowledgmentText={
                                            block.acknowledgmentText
                                        }
                                        editMode={false}
                                        width={block.width}
                                        height={block.height}
                                        dueDate={block.dueDate}
                                        data={block}
                                        navigationStack={navigationStack}
                                        displayTitle={block.displayTitle}
                                        isMobile={true}
                                        portalName={portalName}
                                        portalId={portalId}
                                        onShowPortalView={onShowPortalView}
                                        isPreview={isPreview}
                                        isViewLive={isViewLive}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div
                        className="freeform-grid-display"
                        style={{
                            height: `${gridHeight}px`,
                            width: `${GRID_WIDTH}px`,
                            margin: '0 auto',
                        }}
                    >
                        {items.map(item => (
                            <div key={item} className="grid-item-display"></div>
                        ))}

                        {contentBlocks.map(block => {
                            const startCol = block.start.col + 1;
                            const endCol = block.end.col + 2;
                            const startRow = block.start.row + 1;
                            const endRow = block.end.row + 2;

                            let backgroundColor =
                                block.childPortalPart?.backgroundColor ||
                                'white';
                            let isChildPortalWithIcon = false;

                            if (
                                block.type === 10 &&
                                block.childPortalPart !== null
                            ) {
                                if (block.childPortalPart.glyph !== null) {
                                    isChildPortalWithIcon = true;
                                }
                            } else if (
                                block.type === 11 &&
                                block.linkPart !== null
                            ) {
                                if (block.linkPart.glyph !== null) {
                                    backgroundColor =
                                        block.linkPart.backgroundColor;
                                    isChildPortalWithIcon = true;
                                }
                            }

                            return (
                                <div
                                    key={
                                        block.temporaryId || `block-${block.id}`
                                    }
                                    id={`block-${block.temporaryId || block.id}`}
                                    className="content-block"
                                    style={{
                                        gridColumn: `${startCol} / ${endCol}`,
                                        gridRow: `${startRow} / ${endRow}`,
                                        width: `${block.width}px`,
                                        height: `${block.height}px`,
                                        position: 'absolute',
                                    }}
                                >
                                    <Component
                                        ID={block.id}
                                        Name={block.name}
                                        Subtitle={block.title}
                                        ContentTypeID={block.type}
                                        HelpBoxText={block.helpBoxText}
                                        AcknowledgmentReq={
                                            block.acknowledgmentType
                                        }
                                        AcknowledgmentText={
                                            block.acknowledgmentText
                                        }
                                        editMode={false}
                                        width={block.width}
                                        height={block.height}
                                        dueDate={block.dueDate}
                                        data={block}
                                        navigationStack={navigationStack}
                                        saveDimensions={() => {}}
                                        displayTitle={block.displayTitle}
                                        isMobile={false}
                                        portalName={portalName}
                                        portalId={portalId}
                                        onShowPortalView={onShowPortalView}
                                        isPreview={isPreview}
                                        isViewLive={isViewLive}
                                    />
                                </div>
                            );
                        })}

                        {/* Overlay layer — red border and overlay for overlapping containers in Preview */}
                        {isPreview && (
                            <>
                                {/* Red overlay */}
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: `${GRID_WIDTH}px`,
                                        height: `${gridHeight}px`,
                                        opacity: 0.3,
                                        pointerEvents: 'none',
                                        zIndex: 10,
                                    }}
                                >
                                    {contentBlocks
                                        .filter(block =>
                                            overlappingIds.has(
                                                block.temporaryId,
                                            ),
                                        )
                                        .map(block => (
                                            <div
                                                key={`overlay-${block.temporaryId || block.id}`}
                                                style={{
                                                    position: 'absolute',
                                                    left: `${block.start.col * (cellSize + GAP)}px`,
                                                    top: `${block.start.row * (cellSize + GAP)}px`,
                                                    width: `${block.width}px`,
                                                    height: `${block.height}px`,
                                                    backgroundColor:
                                                        'rgb(255, 0, 0)',
                                                    borderRadius: '10px',
                                                }}
                                            />
                                        ))}
                                </div>
                                {/* Red border overlays for overlapping containers */}
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: `${GRID_WIDTH}px`,
                                        height: `${gridHeight}px`,
                                        pointerEvents: 'none',
                                        zIndex: 500,
                                    }}
                                >
                                    {contentBlocks
                                        .filter(block =>
                                            overlappingIds.has(
                                                block.temporaryId,
                                            ),
                                        )
                                        .map(block => (
                                            <div
                                                key={`overlap-border-${block.temporaryId}`}
                                                style={{
                                                    position: 'absolute',
                                                    left: `${block.start.col * (cellSize + GAP)}px`,
                                                    top: `${block.start.row * (cellSize + GAP)}px`,
                                                    width: `${block.width}px`,
                                                    height: `${block.height}px`,
                                                    border: '1px solid red',
                                                    backgroundColor:
                                                        'rgba(255, 0, 0, 0.2)',
                                                    borderRadius: '10px',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                        ))}
                                </div>
                            </>
                        )}
                    </div>
                ))}
        </div>
    );
};

export default FreeformGrid;
